import React, { useLayoutEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";

dayjs.extend(customParseFormat);
import {
  Button,
  Modal,
  ModalBody,
  Form,
  Input,
  Label,
  FormGroup,
  FormFeedback,
  Spinner,
} from "reactstrap";

const toDateValue = (value) => {
  if (!value) return dayjs().format("YYYY-MM-DD");
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format("YYYY-MM-DD") : dayjs().format("YYYY-MM-DD");
};

const toTimeValue = (value) => {
  if (!value) return dayjs().format("HH:mm");
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format("HH:mm") : dayjs().format("HH:mm");
};

/**
 * Prefer API `scheduled` for date/time. Handles ISO strings and US-style datetimes;
 * also repairs values like "11/29/2025 12:00:00 AM16:10" where an HH:mm suffix was glued after AM/PM.
 */
const parseScheduledForModal = (scheduled, requestDateFallback) => {
  const raw = String(scheduled ?? requestDateFallback ?? "").trim();
  if (!raw) {
    return {
      requestDate: dayjs().format("YYYY-MM-DD"),
      requestTime: dayjs().format("HH:mm"),
    };
  }

  let main = raw;
  let suffixTime = null;
  const glued = raw.match(/^(.+\b(?:AM|PM))(\d{1,2}:\d{2}(?::\d{2})?)$/i);
  if (glued) {
    main = glued[1].trim();
    suffixTime = glued[2];
  }

  const formats = [
    "MM/DD/YYYY h:mm:ss A",
    "MM/DD/YYYY hh:mm:ss A",
    "M/D/YYYY h:mm:ss A",
    "MM/DD/YYYY H:mm:ss",
    "YYYY-MM-DDTHH:mm:ss.SSS",
    "YYYY-MM-DDTHH:mm:ss",
  ];
  let d = dayjs(main, formats, true);
  if (!d.isValid()) d = dayjs(main);

  let requestDate = d.isValid() ? d.format("YYYY-MM-DD") : toDateValue(requestDateFallback);
  let requestTime = d.isValid() ? d.format("HH:mm") : toTimeValue(requestDateFallback);

  if (suffixTime) {
    const t = dayjs(suffixTime, ["H:mm:ss", "HH:mm:ss", "H:mm", "HH:mm"], true);
    if (t.isValid()) requestTime = t.format("HH:mm");
  }

  return { requestDate, requestTime };
};

/** Map API / table values to the two radio values used in this modal. */
const normalizestatus = (raw) => {
  const item = String(raw ?? "")
    .trim()
    .toLowerCase();
  if (
    ["deactive", "inactive", "in_active", "disabled", "0", "false", "no", false].includes(item) ||
    item === "deactive"
  ) {
    return "deactive";
  }
  return "active";
};

const defaultForm = {
  id: "",
  candidateClientId: "",
  fullName: "",
  phoneNumber: "",
  requestDate: dayjs().format("YYYY-MM-DD"),
  requestTime: dayjs().format("HH:mm"),
  zone: "America/Chicago",
  notes: "",
  status: "",
  updatedBy: 0,
};

const CandidateCallBackModal = ({
  setCandidateCallbackAddEditModalOpen,
  candidateCallbackAddEditModalOpen,
  editableData = null,
  onSubmit,
  submitting = false,
}) => {
  const [formData, setFormData] = useState({ ...defaultForm });
  const [errors, setErrors] = useState({});

  useLayoutEffect(() => {
    if (!candidateCallbackAddEditModalOpen) return;
    const source = editableData || {};
    const { requestDate, requestTime } = parseScheduledForModal(
      source?.scheduled,
      source?.requestDate
    );
    setFormData({
      id: source?.id || "",
      candidateClientId: source?.candidateClientId || source?.clientId || "",
      fullName:
        source?.fullName ||
        source?.name ||
        source?.candidateName ||
        source?.candidateClientName ||
        source?.client ||
        "",
      phoneNumber: source?.phoneNumber || source?.phone || source?.mobile || "",
      requestDate,
      requestTime,
      zone: source?.zone || source?.timeZone || "America/Chicago",
      notes: source?.notes || "",
      status: normalizestatus(
        source?.status ?? source?.status ?? defaultForm.status
      ),
      updatedBy: Number(source?.updatedBy || 0),
    });
    setErrors({});
  }, [editableData, candidateCallbackAddEditModalOpen]);

  const modalTitle = useMemo(
    () => (formData?.id ? "CALL BACK REQUEST" : "CALL BACK REQUEST"),
    [formData?.id]
  );

  const handleChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => {
      if (!prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
  };

  const validate = () => {
    const nextErrors = {};
    if (!String(formData?.requestDate || "").trim()) nextErrors.requestDate = "Date is required.";
    if (!String(formData?.requestTime || "").trim()) nextErrors.requestTime = "Time is required.";
    if (!String(formData?.notes || "").trim()) nextErrors.notes = "Notes is required.";
    if (!String(formData?.status || "").trim()) nextErrors.status = "Status is required.";
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;

    const payload = {
      id: String(formData?.id || ""),
      status: formData?.status=='deactive' ?  false : true,
    };

    if (typeof onSubmit === "function") onSubmit(payload);
  };

  const closeModal = () => {
    if (submitting) return;
    setCandidateCallbackAddEditModalOpen(false);
  };

  return (
    <Modal
      isOpen={candidateCallbackAddEditModalOpen}
      toggle={closeModal}
      backdrop="static"
      className="modal-full-height"
      centered
    >
      <ModalBody className="p-4" style={{ maxHeight: "95vh", overflowY: "auto" }}>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="fw-bold mb-0">{modalTitle}</h5>
          <Button close onClick={closeModal} />
        </div>
        <hr />

        <Form
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit();
          }}
        >
          <FormGroup>
            <Label>
              Full Name <span className="text-danger">*</span>
            </Label>
            <Input type="text" value={formData.fullName} disabled />
          </FormGroup>

          <FormGroup>
            <Label>
              Phone number <span className="text-danger">*</span>
            </Label>
            <Input type="text" value={formData.phoneNumber} disabled />
          </FormGroup>

          <FormGroup>
            <Label>
              Date <span className="text-danger">*</span>
            </Label>
            <Input
              type="date"
              value={formData.requestDate}
              invalid={!!errors.requestDate}
              onChange={(e) => handleChange("requestDate", e.target.value)}
              disabled
            />
            <FormFeedback>{errors.requestDate}</FormFeedback>
          </FormGroup>

          <FormGroup>
            <Label>
              Time <span className="text-danger">*</span>
            </Label>
            <Input
              type="time"
              value={formData.requestTime}
              invalid={!!errors.requestTime}
              onChange={(e) => handleChange("requestTime", e.target.value)}
              disabled
            />
            <FormFeedback>{errors.requestTime}</FormFeedback>
          </FormGroup>

          <FormGroup>
            <Label>Zone</Label>
            <Input
              type="text"
              value={formData.zone}
              onChange={(e) => handleChange("zone", e.target.value)}
              disabled
            />
          </FormGroup>

          <FormGroup>
            <Label>
              Notes <span className="text-danger">*</span>
            </Label>
            <Input
              type="textarea"
              rows={3}
              value={formData.notes}
              invalid={!!errors.notes}
              onChange={(e) => handleChange("notes", e.target.value)}
              disabled
            />
            <FormFeedback>{errors.notes}</FormFeedback>
          </FormGroup>

          <FormGroup>
            <Label>
              Status <span className="text-danger">*</span>
            </Label>
            <div
              className="input-box d-flex align-items-center justify-content-between"
              key={`call-back-status-group-${formData.id || "new"}-${candidateCallbackAddEditModalOpen ? "open" : "closed"}`}
            >
              <span className="text-muted">Type</span>
              <div>
                <Input
                  key={`call-back-status-active-${formData.id || "new"}`}
                  type="radio"
                  name="status"
                  id="call-back-status-active"
                  defaultChecked={formData.status === "active"}
                  onChange={() => handleChange("status", "active")}
                />{" "}
                Active
                <Input
                  key={`call-back-status-deactive-${formData.id || "new"}`}
                  type="radio"
                  name="status"
                  id="call-back-status-deactive"
                  defaultChecked={formData.status === "deactive"}
                  onChange={() => handleChange("status", "deactive")}
                  className="ms-3"
                />{" "}
                Deactive
              </div>
            </div>
          </FormGroup>

          <div className="text-end mt-3">
            <Button color="primary" type="submit" disabled={submitting}>
              {submitting ? (
                <>
                  <Spinner size="sm" className="me-1" /> Updating...
                </>
              ) : (
                "Update"
              )}
            </Button>
          </div>
        </Form>
      </ModalBody>
    </Modal>
  );
};

export default CandidateCallBackModal;
